import { Injectable } from '@angular/core';
import { activeFastPlay } from '@app/state/actions/global-scope.actions';
import { AppState } from '@app/state/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Subscription, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, take, tap } from 'rxjs/operators';

import { CountryService } from '../country/country';
import { DeviceKS } from '../device-ks/device-ks';
import { KSFirebase } from '../firebase/ksfirebase';
import { LocationService } from '../helpers/location';
import { LocalStorage } from '../local-storage/local-storage';
import { PremiumCheckerService } from './premium-checker.service';
import { SessionService } from './session.service';
import { User } from './user';

@Injectable()
export class AuthTokenService {
  public token$ = new BehaviorSubject(null);

  /**
   * Subscripción del launchEvent
   */
  protected launchDataSubscription: Subscription | undefined;

  constructor(
    private country: CountryService,
    public deviceks: DeviceKS,
    private sessionFirebase: KSFirebase,
    private session: SessionService,
    private location: LocationService,
    private localStorage: LocalStorage,
    private translate: TranslateService,
    private userService: User,
    private premiumChecker: PremiumCheckerService,
    private store: Store<AppState>
  ) {}

  load(): Promise<any> {
    return new Promise(resolve => {
      const newSession = this.location.getUrlParameter('newSession');

      if (newSession) {
        this.removeTokens();
      }

      const newActiveFastPlay = this.location.getUrlParameter('fastPlay') === 'true';
      this.store.dispatch(activeFastPlay({ activeFastPlayValue: newActiveFastPlay }));

      this.resolveSession()
        .pipe(take(1))
        .subscribe(() => {
          const launchEvent = this.deviceks.devicePlatform.lauchEvent.value;
          console.log('AUTH-TOKEN : launchEvent', launchEvent);
          this.session.syncLaunchDataInFirestore(launchEvent).catch(error => {
            console.error('error al sincronizar launchEvent', error);
          });

          resolve(true);
        });
    });
  }

  resolveSession() {
    return combineLatest([this.country.country, this.deviceks.DUID]).pipe(
      filter(([country, tv_id]) => !!country && !!tv_id),
      distinctUntilChanged((previous, current) => previous[0] === current[0] && previous[1] === current[1]),
      take(1),
      switchMap(async ([, tv_id]) => {
        // // TODO: Check if has launched from mobile
        const launchEvent = this.deviceks.devicePlatform.lauchEvent.value;
        //   //TODO: checkear si es que hay un usuario diferente al que esta logueado
        if (launchEvent?.tokenUser) {
          const mobileTokenUser = launchEvent.tokenUser;
          const tvTokenUser = await this.localStorage.getItem('tokenUser');
          if (!!tvTokenUser && mobileTokenUser !== tvTokenUser) {
            // check TV token user
            const tvPromise = this.premiumChecker.check(tvTokenUser);
            const mobilePromise = this.premiumChecker.check(mobileTokenUser);

            await Promise.all([tvPromise, mobilePromise]).then(async ([tvResponse, mobileResponse]) => {
              //movil premiun to a tv free
              if (!tvResponse.premium && mobileResponse.premium) {
                await this.localStorage.setItem('tokenUser', mobileTokenUser);
                this.localStorage.removeItem('tokenTv');
              }

              //movil free to a tv free
              // no esta redirigiendo
              if (!tvResponse.premium && !mobileResponse.premium) {
                await this.localStorage.setItem('tokenUser', mobileTokenUser);
                await this.localStorage.setItem('isCastFreeUsers', true);
                this.localStorage.removeItem('tokenTv');
              }
            });
            return tv_id;
          }
          return tv_id;
        }

        return tv_id;
      }),
      switchMap(tv_id =>
        this.session.initSession(tv_id).pipe(
          tap(async session => {
            // se espera a que inicialize el usuario antes de suscribirse a firebase
            await this.userService.initUser(session);
            this.sessionFirebase.init();
          }),
          catchError(this.errorInitSession.bind(this))
        )
      )
    );
  }

  errorInitSession(error) {
    console.error('🚧 errorInitSession', error); // DEBUG 🚧
    this.translate.use(this.translate.currentLang || this.translate.getBrowserLang());
    this.translate.get(['APP_NOT_AVAILABLE', 'RETRY']).subscribe(msgs => {
      this.userService.messages = msgs;
      const modalAux = document.getElementById('modalAux');
      (window as any).onRetry = this.userService.onRetry.bind(this.userService);
      (window as any).doReload = this.userService.doReload.bind(this.userService);
      if (modalAux) {
        modalAux.innerHTML =
          `<p class="error-on-init"><br/><span class="face-sad">:(</span><br/><br/>` +
          `<br/>${this.userService.messages['APP_NOT_AVAILABLE']}</p><br/><br/>` +
          `<button id="retry-btn" class="btn btn-retry-reload">` +
          `${this.userService.messages['RETRY']}</button>`;
      }
      this.userService.sessionError = true;
      const loader = document.getElementsByTagName('smart-router-animation-wrap')[0];
      if (loader) {
        loader.remove();
      }
      const modalInits = document.getElementsByClassName('modal-init');
      let i: number;
      for (i = 0; i < modalInits.length; i++) {
        (modalInits[i] as HTMLElement).style.display = 'none';
      }
      const buttonRetry = document.getElementById('retry-btn');
      buttonRetry.addEventListener('click', this.userService.doReload.bind(this));
      buttonRetry.focus();
      document.addEventListener('keypress', this.userService.onRetry);
      document.addEventListener('keydown', this.userService.onRetry);
      setTimeout(() => {
        try {
          const loader2 = document.getElementsByTagName('smart-router-animation-wrap')[0];
          loader2.remove();
          (loader2 as HTMLElement).style.display = 'none';
        } catch (e) {}
      }, 1000);
    });
    return throwError(error);
  }

  removeTokens() {
    this.localStorage.removeItem('country');
    this.localStorage.removeItem('session');
    this.localStorage.removeItem('tokenTv');
    this.localStorage.removeItem('tokenUser');
    this.localStorage.removeItem('selectedGenres');
  }

  removeAllStorage() {
    this.removeTokens();
    this.localStorage.removeAll();
  }
}

export const authTokenFactory =
  (service: AuthTokenService): (() => Promise<any>) =>
  () =>
    service.load();
